/**
 * Margin Forecasting & Business Advisor Components
 * 
 * NON-NEGOTIABLE UI RULES:
 * - Source attribution: Every number shows where it came from
 * - Confidence indicators: Always show data confidence level
 * - Missing data: Explicitly shown, never hidden
 * - Advisory tone: Suggestions only, no prescriptions
 */
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Info, HelpCircle,
  DollarSign, Package, Clock, Minus, CheckCircle, XCircle,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ SOURCE BADGE ============

/**
 * Shows the data source for a value
 */
export function SourceBadge({ source, size = 'sm' }) {
  const sourceColors = {
    QUOTE: { bg: 'rgba(59, 130, 246, 0.15)', text: 'rgb(59, 130, 246)' },
    JOB: { bg: 'rgba(34, 197, 94, 0.15)', text: 'rgb(34, 197, 94)' },
    PO: { bg: 'rgba(249, 115, 22, 0.15)', text: 'rgb(249, 115, 22)' },
    BANK: { bg: 'rgba(168, 85, 247, 0.15)', text: 'rgb(168, 85, 247)' },
    XERO: { bg: 'rgba(14, 165, 233, 0.15)', text: 'rgb(14, 165, 233)' },
    MANUAL: { bg: 'rgba(156, 163, 175, 0.15)', text: 'rgb(156, 163, 175)' },
    INVOICE: { bg: 'rgba(236, 72, 153, 0.15)', text: 'rgb(236, 72, 153)' },
    UNKNOWN: { bg: 'rgba(107, 114, 128, 0.15)', text: 'rgb(107, 114, 128)' }
  };

  const colors = sourceColors[source] || sourceColors.UNKNOWN;
  const fontSize = size === 'sm' ? '0.625rem' : '0.75rem';

  return (
    <span
      data-testid={`source-badge-${source}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '2px 6px',
        borderRadius: '4px',
        background: colors.bg,
        color: colors.text,
        fontSize,
        fontWeight: 500,
        textTransform: 'uppercase'
      }}
    >
      {source}
    </span>
  );
}

// ============ CONFIDENCE INDICATOR ============

/**
 * Shows confidence level in the data
 */
export function ConfidenceIndicator({ confidence, showLabel = true }) {
  const configs = {
    HIGH: { color: 'var(--status-success)', icon: CheckCircle, label: 'High Confidence' },
    MEDIUM: { color: 'var(--status-warning)', icon: AlertTriangle, label: 'Partial Data' },
    LOW: { color: 'var(--status-error)', icon: Info, label: 'Limited Data' },
    INSUFFICIENT: { color: 'var(--text-muted)', icon: HelpCircle, label: 'Insufficient Data' }
  };

  const config = configs[confidence] || configs.INSUFFICIENT;
  const Icon = config.icon;

  return (
    <div
      data-testid={`confidence-${confidence}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        color: config.color,
        fontSize: '0.75rem'
      }}
    >
      <Icon size={12} />
      {showLabel && <span>{config.label}</span>}
    </div>
  );
}

// ============ SOURCED VALUE DISPLAY ============

/**
 * Displays a value with its source attribution
 */
export function SourcedValue({ 
  label, 
  value, 
  source, 
  isEstimate = false,
  description,
  format = 'currency'
}) {
  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    if (format === 'currency') return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (format === 'percentage') return `${val.toFixed(1)}%`;
    return val.toString();
  };

  return (
    <div 
      data-testid={`sourced-value-${label?.toLowerCase().replace(/\s/g, '-')}`}
      style={{ marginBottom: 'var(--space-sm)' }}
    >
      <div style={{ 
        fontSize: '0.75rem', 
        color: 'var(--text-muted)', 
        marginBottom: '2px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>{label}</span>
        {isEstimate && (
          <span style={{ 
            color: 'var(--status-warning)', 
            fontSize: '0.625rem',
            fontStyle: 'italic'
          }}>
            (estimated)
          </span>
        )}
      </div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-sm)' 
      }}>
        <span style={{ 
          fontSize: '1.25rem', 
          fontWeight: 600,
          color: value < 0 ? 'var(--status-error)' : 'var(--text-primary)'
        }}>
          {formatValue(value)}
        </span>
        {source && <SourceBadge source={source} />}
      </div>
      {description && (
        <div style={{ 
          fontSize: '0.6875rem', 
          color: 'var(--text-muted)',
          marginTop: '2px'
        }}>
          {description}
        </div>
      )}
    </div>
  );
}

// ============ MISSING DATA NOTICE ============

/**
 * Explicitly shows what data is missing
 */
export function MissingDataNotice({ missingData }) {
  if (!missingData || missingData.length === 0) return null;

  return (
    <div 
      data-testid="missing-data-notice"
      style={{
        background: 'rgba(234, 179, 8, 0.1)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-sm) var(--space-md)',
        marginBottom: 'var(--space-md)'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        marginBottom: 'var(--space-xs)',
        color: 'var(--status-warning)',
        fontWeight: 500,
        fontSize: '0.75rem'
      }}>
        <Info size={14} />
        <span>Data Gaps ({missingData.length})</span>
      </div>
      <ul style={{ 
        margin: 0, 
        paddingLeft: 'var(--space-md)',
        fontSize: '0.75rem',
        color: 'var(--text-secondary)'
      }}>
        {missingData.map((item, idx) => (
          <li key={idx} style={{ marginBottom: '4px' }}>
            <strong>{item.field_name}:</strong> {item.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============ DRIFT ALERT ============

/**
 * Shows cost drift/overrun warning
 */
export function DriftAlert({ drift }) {
  const severityColors = {
    HIGH: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', icon: 'var(--status-error)' },
    MEDIUM: { bg: 'rgba(234, 179, 8, 0.15)', border: 'rgba(234, 179, 8, 0.3)', icon: 'var(--status-warning)' },
    LOW: { bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.3)', icon: 'var(--text-muted)' }
  };

  const colors = severityColors[drift.severity] || severityColors.LOW;

  return (
    <div 
      data-testid={`drift-alert-${drift.drift_type}`}
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-sm) var(--space-md)',
        marginBottom: 'var(--space-sm)'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px' 
      }}>
        <AlertTriangle size={16} style={{ color: colors.icon }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {drift.drift_type.replace(/_/g, ' ')}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {drift.description}
          </div>
        </div>
        <span style={{ 
          fontSize: '0.625rem', 
          fontWeight: 600,
          textTransform: 'uppercase',
          color: colors.icon
        }}>
          {drift.severity}
        </span>
      </div>
    </div>
  );
}

// ============ JOB MARGIN CARD ============

/**
 * Displays margin analysis for a single job
 */
export function JobMarginCard({ jobId, orgId, compact = false }) {
  const { token } = useAuth();
  const [marginData, setMarginData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMargin = async () => {
      if (!token || !jobId || !orgId) return;
      
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/api/margin/job/${jobId}`,
          {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setMarginData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load margin data');
      } finally {
        setLoading(false);
      }
    };

    fetchMargin();
  }, [token, jobId, orgId]);

  if (loading) {
    return (
      <div data-testid="job-margin-loading" style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
        <Clock size={16} className="animate-spin" />
        <span style={{ marginLeft: '8px', fontSize: '0.875rem' }}>Calculating margin...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="job-margin-error" style={{ 
        padding: 'var(--space-md)', 
        color: 'var(--status-error)',
        fontSize: '0.875rem'
      }}>
        <Info size={14} style={{ marginRight: '6px' }} />
        {error}
      </div>
    );
  }

  if (!marginData) return null;

  const { job_margin, quote_margin, drifts_detected, confidence, missing_data, data_sources_used } = marginData;

  if (compact) {
    return (
      <div 
        data-testid="job-margin-compact"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Live Margin</div>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 700,
              color: job_margin?.live_margin_net?.value < 0 ? 'var(--status-error)' : 'var(--status-success)'
            }}>
              {job_margin?.live_margin_net?.value !== undefined 
                ? `£${job_margin.live_margin_net.value.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                : '—'
              }
              {job_margin?.live_margin_percentage !== undefined && (
                <span style={{ fontSize: '0.875rem', marginLeft: '6px' }}>
                  ({job_margin.live_margin_percentage}%)
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <ConfidenceIndicator confidence={confidence} showLabel={false} />
            {drifts_detected?.length > 0 && (
              <span style={{ 
                fontSize: '0.625rem', 
                color: 'var(--status-warning)',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <AlertTriangle size={10} />
                {drifts_detected.length} alert(s)
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="job-margin-card"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-lg)'
      }}
    >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: 'var(--space-md)'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Margin Analysis</h3>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Sources: {data_sources_used?.map(s => s).join(', ')}
          </div>
        </div>
        <ConfidenceIndicator confidence={confidence} />
      </div>

      {/* Missing Data Warning */}
      <MissingDataNotice missingData={missing_data} />

      {/* Drift Alerts */}
      {drifts_detected?.length > 0 && (
        <div style={{ marginBottom: 'var(--space-md)' }}>
          {drifts_detected.map((drift, idx) => (
            <DriftAlert key={idx} drift={drift} />
          ))}
        </div>
      )}

      {/* Main Margin Display */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: 'var(--space-lg)',
        marginBottom: 'var(--space-md)'
      }}>
        {/* Quote Expected */}
        <div>
          <div style={{ 
            fontSize: '0.6875rem', 
            color: 'var(--text-muted)', 
            textTransform: 'uppercase',
            marginBottom: 'var(--space-sm)'
          }}>
            Expected (Quote)
          </div>
          {quote_margin ? (
            <>
              <SourcedValue 
                label="Quoted Price"
                value={quote_margin.quoted_price_net?.value}
                source={quote_margin.quoted_price_net?.source}
              />
              {quote_margin.expected_margin_net && (
                <SourcedValue 
                  label="Expected Margin"
                  value={quote_margin.expected_margin_net.value}
                  source={quote_margin.expected_margin_net.source}
                  description={quote_margin.expected_margin_percentage ? `${quote_margin.expected_margin_percentage}% of quote` : null}
                />
              )}
            </>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              No quote data available
            </div>
          )}
        </div>

        {/* Live Actual */}
        <div>
          <div style={{ 
            fontSize: '0.6875rem', 
            color: 'var(--text-muted)', 
            textTransform: 'uppercase',
            marginBottom: 'var(--space-sm)'
          }}>
            Live (Actual)
          </div>
          <SourcedValue 
            label="Actual Costs"
            value={job_margin?.actual_po_total?.value}
            source={job_margin?.actual_po_total?.source}
          />
          <SourcedValue 
            label="Live Margin"
            value={job_margin?.live_margin_net?.value}
            source={job_margin?.live_margin_net?.source}
            description={job_margin?.live_margin_percentage ? `${job_margin.live_margin_percentage}% of quoted` : null}
          />
        </div>
      </div>

      {/* Calculation Breakdown */}
      {job_margin?.calculation_breakdown && (
        <div style={{
          background: 'rgba(0,0,0,0.2)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-sm) var(--space-md)',
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          color: 'var(--text-secondary)',
          whiteSpace: 'pre-wrap'
        }}>
          {job_margin.calculation_breakdown}
        </div>
      )}
    </div>
  );
}

// ============ BUSINESS MARGIN PANEL ============

/**
 * Displays business-level margin overview
 */
export function BusinessMarginPanel({ orgId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !orgId) return;
      
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/api/margin/business`,
          {
            params: { org_id: orgId, period_days: periodDays },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load business margin');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, orgId, periodDays]);

  if (loading) {
    return (
      <div data-testid="business-margin-loading" style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
        <Clock size={20} className="animate-spin" />
        <div style={{ marginTop: 'var(--space-sm)', color: 'var(--text-muted)' }}>
          Analyzing business margin...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="business-margin-error" style={{ 
        padding: 'var(--space-lg)', 
        textAlign: 'center',
        color: 'var(--status-error)'
      }}>
        <AlertTriangle size={24} />
        <div style={{ marginTop: 'var(--space-sm)' }}>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { business_margin, health_indicators, confidence, missing_data, founder_summary } = data;
  const margin = business_margin;

  return (
    <div 
      data-testid="business-margin-panel"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{ 
        padding: 'var(--space-md) var(--space-lg)',
        borderBottom: '1px solid var(--glass-border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <BarChart3 size={20} style={{ color: 'var(--accent-primary)' }} />
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Business Performance</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 8px',
              color: 'var(--text-primary)',
              fontSize: '0.75rem'
            }}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <ConfidenceIndicator confidence={confidence} />
        </div>
      </div>

      {/* Missing Data */}
      {missing_data?.length > 0 && (
        <div style={{ padding: 'var(--space-md) var(--space-lg) 0' }}>
          <MissingDataNotice missingData={missing_data} />
        </div>
      )}

      {/* Main Metrics */}
      <div style={{ padding: 'var(--space-lg)' }}>
        {/* Gross Margin Hero */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: 'var(--space-lg)',
          padding: 'var(--space-lg)',
          background: margin?.gross_margin?.value >= 0 
            ? 'rgba(34, 197, 94, 0.1)' 
            : 'rgba(239, 68, 68, 0.1)',
          borderRadius: 'var(--radius-md)'
        }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
            Gross Margin
          </div>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 700,
            color: margin?.gross_margin?.value >= 0 ? 'var(--status-success)' : 'var(--status-error)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)'
          }}>
            {margin?.gross_margin?.value >= 0 
              ? <TrendingUp size={28} />
              : <TrendingDown size={28} />
            }
            £{Math.abs(margin?.gross_margin?.value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
          </div>
          {margin?.gross_margin_percentage !== undefined && (
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: 'var(--space-xs)' }}>
              {margin.gross_margin_percentage}% margin
            </div>
          )}
          <div style={{ marginTop: 'var(--space-sm)' }}>
            <SourceBadge source={margin?.gross_margin?.source} />
          </div>
        </div>

        {/* Revenue vs Costs */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: 'var(--space-lg)',
          marginBottom: 'var(--space-lg)'
        }}>
          <div>
            <SourcedValue 
              label="Total Invoiced"
              value={margin?.total_invoiced?.value}
              source={margin?.total_invoiced?.source}
              description="Revenue in period"
            />
          </div>
          <div>
            <SourcedValue 
              label="Total Costs"
              value={(margin?.total_supplier_bills?.value || 0) + (margin?.total_material_spend?.value || 0)}
              source="COMBINED"
              description="Bills + materials"
            />
          </div>
        </div>

        {/* Health Indicators */}
        {health_indicators?.length > 0 && (
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              marginBottom: 'var(--space-sm)',
              textTransform: 'uppercase'
            }}>
              Health Signals
            </div>
            {health_indicators.filter(h => h.is_triggered).map((indicator, idx) => (
              <div 
                key={idx}
                style={{
                  background: 'rgba(234, 179, 8, 0.1)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-sm) var(--space-md)',
                  marginBottom: 'var(--space-xs)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <AlertTriangle size={14} style={{ color: 'var(--status-warning)', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {indicator.indicator_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {indicator.explanation}
                    </div>
                    {indicator.suggestion && (
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--accent-primary)',
                        fontStyle: 'italic',
                        marginTop: '4px'
                      }}>
                        {indicator.suggestion}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Founder Summary */}
        {founder_summary && (
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-md)',
            borderLeft: '3px solid var(--accent-primary)'
          }}>
            <div style={{ 
              fontSize: '0.6875rem', 
              color: 'var(--text-muted)', 
              marginBottom: 'var(--space-xs)',
              textTransform: 'uppercase'
            }}>
              Summary
            </div>
            <div style={{ 
              fontSize: '0.875rem', 
              color: 'var(--text-primary)',
              lineHeight: 1.5
            }}>
              {founder_summary}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ MARGIN SUMMARY WIDGET ============

/**
 * Compact widget for Command Centre
 */
export function MarginSummaryWidget({ orgId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !orgId) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/api/margin/summary`,
          {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setData(response.data);
      } catch (err) {
        console.error('Failed to load margin summary:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, orgId]);

  if (loading) {
    return (
      <div className="widget-card" data-testid="margin-widget-loading">
        <div style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
          <Clock size={16} className="animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="widget-card" data-testid="margin-widget-error">
        <div className="widget-header">
          <div className="widget-icon"><BarChart3 size={20} /></div>
        </div>
        <div className="widget-label">Business Margin</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
          {data?.founder_summary || 'Insufficient data'}
        </div>
      </div>
    );
  }

  const isPositive = data.gross_margin?.value >= 0;

  return (
    <div className="widget-card" data-testid="margin-widget">
      <div className="widget-header">
        <div className="widget-icon">
          <BarChart3 size={20} />
        </div>
        <ConfidenceIndicator confidence={data.gross_margin?.confidence} showLabel={false} />
      </div>
      <div className="widget-label">Gross Margin (30d)</div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-sm)',
        marginTop: 'var(--space-sm)'
      }}>
        {isPositive ? (
          <ArrowUpRight size={20} style={{ color: 'var(--status-success)' }} />
        ) : (
          <ArrowDownRight size={20} style={{ color: 'var(--status-error)' }} />
        )}
        <span style={{ 
          fontSize: '1.5rem', 
          fontWeight: 700,
          color: isPositive ? 'var(--status-success)' : 'var(--status-error)'
        }}>
          £{Math.abs(data.gross_margin?.value || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}
        </span>
        {data.gross_margin?.percentage !== undefined && (
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            ({data.gross_margin.percentage}%)
          </span>
        )}
      </div>
      {data.health_alerts > 0 && (
        <div style={{ 
          marginTop: 'var(--space-sm)',
          fontSize: '0.75rem',
          color: 'var(--status-warning)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <AlertTriangle size={12} />
          {data.health_alerts} alert(s)
        </div>
      )}
      {data.jobs_at_risk > 0 && (
        <div style={{ 
          marginTop: '4px',
          fontSize: '0.75rem',
          color: 'var(--status-error)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <XCircle size={12} />
          {data.jobs_at_risk} job(s) at risk
        </div>
      )}
    </div>
  );
}

export default {
  SourceBadge,
  ConfidenceIndicator,
  SourcedValue,
  MissingDataNotice,
  DriftAlert,
  JobMarginCard,
  BusinessMarginPanel,
  MarginSummaryWidget
};
